import { Component, ReactNode, ReactNodeArray } from 'react';
import { createPortal } from 'react-dom';

type PortalProps = typeof Portal.defaultProps & {
  children: ReactNode | ReactNodeArray;
  isOpen: boolean;
  rootClassname: string;
  isServerSideRendering?: Function;
};

class Portal extends Component<PortalProps> {
  static defaultProps = {
    isServerSideRendering: () => typeof window === 'undefined'
  };

  bodyElem?: HTMLElement;

  modalContainer?: Element;

  constructor(props) {
    super(props);
    const { isServerSideRendering } = this.props;
    if (isServerSideRendering()) {
      return;
    }
    this.bodyElem = document.body;
    this.modalContainer = document.createElement('div');
    this.modalContainer.classList.add(props.rootClassname);
  }

  componentDidMount() {
    const { isOpen } = this.props;
    if (isOpen) {
      this.open();
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props;
    if (!prevProps.isOpen && isOpen) {
      this.open();
    }
    if (prevProps.isOpen && !isOpen) {
      this.close();
    }
    if (isOpen) {
      this.stopBodyScroll();
    }
  }

  componentWillUnmount() {
    const { isOpen } = this.props;
    if (isOpen) {
      this.close();
    }
  }

  stopBodyScroll = () => {
    if (this.bodyElem) this.bodyElem.style.overflow = 'hidden';
  };

  close() {
    if (!this.bodyElem || !this.modalContainer) return;

    this.bodyElem.style.removeProperty('overflow');
    if (this.bodyElem.contains(this.modalContainer)) {
      this.bodyElem.removeChild(this.modalContainer);
    }
  }

  open() {
    if (!this.bodyElem || !this.modalContainer) return;

    this.stopBodyScroll();
    this.bodyElem.appendChild(this.modalContainer);
  }

  render() {
    const { children, isOpen, isServerSideRendering } = this.props;
    const shouldRenderPortal = isOpen && !isServerSideRendering();
    return shouldRenderPortal
      ? createPortal(children, this.modalContainer as Element)
      : null;
  }
}

export default Portal;
